import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "../../components/Page/Page";
import { Profile } from "../User/userDTO";
import { listTargets } from "../StreamTargetLive4tvApi/live4tvApiActions";
import {
    live4tvLive4tvApiService,
    ScheduledLiveResponse,
} from "../StreamTargetLive4tvApi/live4tvLive4tvApiService";

type Properties = {
    profile: Profile;
    listTargets: Function;
};

function ScheduledLivePage(props: Properties) {
    const { profile, listTargets } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [scheduledLives, setScheduledLives] = useState<Array<ScheduledLiveResponse>>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeletingUuid, setIsDeletingUuid] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);

    const formatNetworkName = (networkType: string) => {
        const translated = t(networkType);
        if (translated !== networkType) {
            return translated;
        }

        return networkType
            .split("_")
            .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
            .join(" ");
    };

    const getSocialNetworksSummary = (scheduledLive: ScheduledLiveResponse) => {
        return Array.from(new Set(scheduledLive.accounts.map((account) => formatNetworkName(account.type)))).join(", ");
    };

    const loadScheduledLives = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await live4tvLive4tvApiService.listScheduledLives(profile.token, profile.code);
            setScheduledLives(
                response.data.sort((left, right) => new Date(left.scheduledAt).getTime() - new Date(right.scheduledAt).getTime()),
            );
            setError(null);
        } catch (loadError: any) {
            setError(loadError?.message || t("Failed to load the scheduled live streams."));
        } finally {
            setIsLoading(false);
        }
    }, [profile.code, profile.token, t]);

    useEffect(() => {
        if (profile == null || profile.token === undefined) {
            navigate("/");
            return;
        }

        loadScheduledLives();
    }, [loadScheduledLives, navigate, profile]);

    useEffect(() => {
        const locationState = location.state as { success?: string; warning?: string } | null;
        if (locationState?.success) {
            setSuccess(locationState.success);
        }
        if (locationState?.warning) {
            setWarning(locationState.warning);
        }
        if (locationState?.success || locationState?.warning) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate]);

    const handleDelete = async (scheduledLive: ScheduledLiveResponse) => {
        setIsDeletingUuid(scheduledLive.uuid);
        setError(null);
        setSuccess(null);

        try {
            await live4tvLive4tvApiService.deleteScheduledLive(profile.token, scheduledLive.uuid);
            setSuccess(t("Scheduled live deleted successfully."));
            await loadScheduledLives();
            listTargets({ profile });
        } catch (deleteError: any) {
            setError(deleteError?.message || t("Failed to delete the scheduled live."));
        } finally {
            setIsDeletingUuid(null);
        }
    };

    const renderTable = (items: Array<ScheduledLiveResponse>, emptyMessage: string) => {
        if (items.length === 0) {
            return <Alert severity="info">{emptyMessage}</Alert>;
        }

        return (
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("Title")}</TableCell>
                            <TableCell>{t("Scheduled at")}</TableCell>
                            <TableCell>{t("Social networks")}</TableCell>
                            <TableCell>{t("Accounts")}</TableCell>
                            <TableCell>{t("Status")}</TableCell>
                            <TableCell align="right">{t("Actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((scheduledLive) => (
                            <TableRow key={scheduledLive.uuid}>
                                <TableCell>{scheduledLive.title}</TableCell>
                                <TableCell>{new Date(scheduledLive.scheduledAt).toLocaleString()}</TableCell>
                                <TableCell>{getSocialNetworksSummary(scheduledLive)}</TableCell>
                                <TableCell>{scheduledLive.accounts.map((account) => account.name).join(", ")}</TableCell>
                                <TableCell>{scheduledLive.status}</TableCell>
                                <TableCell align="right">
                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                        {scheduledLive.status === "scheduled" ? (
                                            <Button
                                                size="small"
                                                startIcon={<Edit />}
                                                onClick={() => navigate(`/schedule-live/${scheduledLive.uuid}`)}
                                            >
                                                {t("Edit")}
                                            </Button>
                                        ) : null}
                                        {scheduledLive.status === "scheduled" ? (
                                            <Button
                                                size="small"
                                                color="secondary"
                                                startIcon={<Delete />}
                                                onClick={() => handleDelete(scheduledLive)}
                                                disabled={isDeletingUuid === scheduledLive.uuid}
                                            >
                                                {isDeletingUuid === scheduledLive.uuid ? t("Deleting...") : t("Delete")}
                                            </Button>
                                        ) : null}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const activeScheduledLives = scheduledLives.filter((scheduledLive) => scheduledLive.status !== "completed");
    const completedScheduledLives = scheduledLives.filter((scheduledLive) => scheduledLive.status === "completed");

    return (
        <Page title={t("Scheduled Lives")}>
            <Grid container direction="column" spacing={4}>
                <Grid item>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                        <Box>
                            <Typography variant="h5" gutterBottom>{t("Scheduled Lives")}</Typography>
                            <Typography color="text.secondary">
                                {t("Manage your scheduled and completed live targets.")}
                            </Typography>
                        </Box>
                        <Button variant="contained" onClick={() => navigate("/schedule-live")}>
                            {t("Schedule live")}
                        </Button>
                    </Box>
                </Grid>

                {error ? (
                    <Grid item>
                        <Alert severity="error">{error}</Alert>
                    </Grid>
                ) : null}

                {success ? (
                    <Grid item>
                        <Alert severity="success">{success}</Alert>
                    </Grid>
                ) : null}

                {warning ? (
                    <Grid item>
                        <Alert severity="warning">{warning}</Alert>
                    </Grid>
                ) : null}

                <Grid item>
                    <Typography variant="h5">{t("Scheduled")}</Typography>
                </Grid>

                <Grid item>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress />
                        </Box>
                    ) : renderTable(activeScheduledLives, t("There are no scheduled live streams yet."))}
                </Grid>

                <Grid item>
                    <Typography variant="h5">{t("Completed")}</Typography>
                </Grid>

                <Grid item>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress />
                        </Box>
                    ) : renderTable(completedScheduledLives, t("There are no completed scheduled lives yet."))}
                </Grid>
            </Grid>
        </Page>
    );
}

const mapStateToProps = function (state: any) {
    return {
        profile: state.user.profile,
    };
};

const mapDispatchToProps = function (dispatch: any) {
    return {
        listTargets: (request: any) => dispatch(listTargets(request)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduledLivePage);
